
import React from "react";
import { Transfer, User } from "@/types";
import { formatCurrency } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, X } from "lucide-react";

interface PendingTransfersManagementProps {
  transfers: Transfer[];
  familyMembers: User[];
  onApprove: (transferId: string) => Promise<void>;
  onReject: (transferId: string) => Promise<void>;
  isLoading?: boolean;
}

export function PendingTransfersManagement({ 
  transfers, 
  familyMembers, 
  onApprove, 
  onReject,
  isLoading = false
}: PendingTransfersManagementProps) {
  const pendingTransfers = transfers.filter(t => t.status === 'pending');

  if (pendingTransfers.length === 0) {
    return null;
  }

  const getUserName = (userId: string) => {
    const user = familyMembers.find(u => u.id === userId);
    return user ? `${user.firstName} ${user.lastName}` : 'Unknown User';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pending Transfer Requests</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {pendingTransfers.map((transfer) => (
            <div key={transfer.id} className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex-1">
                <p className="font-medium">
                  {getUserName(transfer.fromUserId)} → {getUserName(transfer.toUserId)}
                </p>
                <p className="text-sm text-muted-foreground">
                  Amount: {formatCurrency(transfer.amount)}
                </p>
                {transfer.description && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Description: {transfer.description}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => onApprove(transfer.id)}
                  disabled={isLoading}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Check className="w-4 h-4 mr-1" />
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => onReject(transfer.id)}
                  disabled={isLoading}
                >
                  <X className="w-4 h-4 mr-1" />
                  Reject
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
